import { useState, useEffect, useRef } from 'react';

interface TypingIndicatorProps {
  visible: boolean;
  /** Minimum time to display once shown (ms). Default: 500ms */
  minDisplayTime?: number;
  /**
   * When false, renders only the animated dots — no "thinking..." label.
   * Used in lobby NPC chat where NPCs are presented as human players;
   * the label would break that illusion.
   */
  showLabel?: boolean;
}

export function TypingIndicator({
  visible,
  minDisplayTime = 1000,
  showLabel = true,
}: TypingIndicatorProps) {
  const [isShowing, setIsShowing] = useState(false);
  const showTimeRef = useRef<number | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (visible) {
      // Show immediately when visible becomes true
      setIsShowing(true);
      showTimeRef.current = Date.now();

      // Clear any pending hide timeout
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    } else if (isShowing && showTimeRef.current) {
      // When visible becomes false, check if min time has passed
      const elapsed = Date.now() - showTimeRef.current;
      const remaining = minDisplayTime - elapsed;

      if (remaining <= 0) {
        // Min time already passed, hide immediately
        setIsShowing(false);
        showTimeRef.current = null;
      } else {
        // Schedule hide after remaining time
        hideTimeoutRef.current = setTimeout(() => {
          setIsShowing(false);
          showTimeRef.current = null;
          hideTimeoutRef.current = null;
        }, remaining);
      }
    }

    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [visible, minDisplayTime, isShowing]);

  // Always render the container to prevent layout shift (visibility: hidden preserves space)
  return (
    <div className={`pb-2 px-1 pt-1 ${isShowing ? '' : 'invisible'}`}>
      <div className="flex items-center gap-2">
        <span className="flex items-center gap-1.5">
          <span className="typing-dot w-2 h-2 bg-amber-500 rounded-full" />
          <span className="typing-dot w-2 h-2 bg-amber-500 rounded-full" />
          <span className="typing-dot w-2 h-2 bg-amber-500 rounded-full" />
        </span>
        {showLabel && (
          <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">
            thinking...
          </span>
        )}
      </div>
    </div>
  );
}
